import React, { useEffect, useRef, useState, memo, useCallback, useMemo } from "react";
import { Button, Card, Typography, Tooltip } from "antd";
import { useRouter } from "next/router";
import CarouselModel from "../../../../components/ImagesCarousel";
import TurfInfoList from "../TurfInfoList";
import { showBookNowDynamic } from "../../../../utils/showFeatures";
import { useCities } from "../../../../contexts/CitiesContext";
import {
  buildTurfBookingRoute,
  buildTurfDetailRoute,
  getAreaFromPath,
} from "../../../../utils/turfRoutes";
import {
  getPitchDimensionsLabel,
  getPitchSpecificationLabel,
} from "../../../../utils/pitchSpecification";
import {
  getPitchSports,
  isSportEnabledAtTurf,
} from "../../../../utils/sport";

interface TileProps {
  name: string;
  userName: string;
  city: string;
  imageUrl: string[];
  sportImages?: Record<string, string[]>;
  supportedSports?: string[];
  activeSport?: string;
  fields: any[];
  data: any;
  isUnavailable?: boolean;
  index: number;
  /** If true, the first image will be loaded with priority (for LCP optimization) */
  isLCP?: boolean;
  /** From user-selected location vs turf DB coordinates; null hides the row */
  distanceLabel?: string | null;
}

const Tile: React.FC<TileProps> = ({
  name,
  userName,
  city,
  imageUrl,
  sportImages,
  activeSport,
  fields,
  data,
  isUnavailable = false,
  index,
  isLCP = false,
  distanceLabel = null,
}) => {
  const { cities } = useCities();
  const router = useRouter();
  const { starRating, partnership, areaPrice } = data;
  const routeArea = useMemo(
    () => getAreaFromPath(router.asPath || ""),
    [router.asPath]
  );
  const areaNameForRoute = routeArea || data?.areaName || null;

  const [selectedPitchName, setSelectedPitchName] = useState<string>(
    areaPrice?.[0]?.pitchName || ""
  );
  const [selectedArea, setSelectedArea] = useState<string>(
    areaPrice?.[0]?.area || ""
  );
  const [selectedPrice, setSelectedPrice] = useState<number>(0);
  const [showPackages, setShowPackages] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef<HTMLDivElement>(null);
  // Cache bounding rect to avoid forced reflows on every mouse move
  const rectCache = useRef<DOMRect | null>(null);

  const { Text, Title } = Typography;

  // Filter fields to display
  const req_fields = fields.filter((field) => {
    const contact = partnership ? "actualContact" : "displayContact";
    return ![
      "availabilityData",
      "isRainProtected",
      "location",
      "address",
      "hasCafe",
      contact,
    ].includes(field.key);
  });

  // Initialize price
  const getDisplayPrice = (priceObj: any) => {
    if (Array.isArray(priceObj)) {
      return priceObj.find((p) => p.day === "Monday")?.price ?? priceObj[0]?.price ?? 0;
    } else if (priceObj?.weekday) {
      return priceObj.weekday.regularHours ?? 0;
    }
    return 0;
  };

  useEffect(() => {
    if (areaPrice && areaPrice.length > 0) {
      setSelectedPitchName(areaPrice[0].pitchName || "");
      setSelectedArea(areaPrice[0].area || "");
      setSelectedPrice(getDisplayPrice(areaPrice[0]?.price));
    }
    setShowPackages(false);
  }, [data, areaPrice]);

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    sessionStorage.setItem("selectedCardIndex", index.toString());
  
    if (!isUnavailable && areaPrice?.length) {
      const url = buildTurfDetailRoute({
        city,
        userName,
        pitchName: areaPrice[0].pitchName,
        areaName: areaNameForRoute,
      });
      router.push(activeSport ? `${url}?sport=${activeSport}` : url);
    }
  };
  // Handle "Check Availability" button
  const handleBookNow = (
    e: React.MouseEvent<HTMLElement>,
    pitchName?: string
  ) => {
    e.stopPropagation();
    const finalPitchName = pitchName || selectedPitchName;
    if (!finalPitchName) return;
    const pitch = areaPrice?.find(
      (item: any) => item?.pitchName === finalPitchName
    );
    const sport = activeSport || getPitchSports(pitch)[0];
    if (sport && !isSportEnabledAtTurf(sport, data?.enabledSports)) return;
    const bookingUrl = buildTurfBookingRoute({
      city,
      userName,
      pitchName: finalPitchName,
      areaName: areaNameForRoute,
    });
    router.push(activeSport ? `${bookingUrl}?sport=${activeSport}` : bookingUrl);
  };

  // Handle selecting a specific package
  const handleTurfCardClick = (index: number) => {
    const selectedPackage = areaPrice?.[index];
    if (!selectedPackage) return;
    setSelectedArea(selectedPackage.area || "");
    setSelectedPrice(getDisplayPrice(selectedPackage.price));
    setSelectedPitchName(selectedPackage.pitchName || "");
    if (!isUnavailable) {
      const url = buildTurfDetailRoute({
        city,
        userName,
        pitchName: selectedPackage.pitchName,
        areaName: areaNameForRoute,
      });
      router.push(activeSport ? `${url}?sport=${activeSport}` : url);
    }
  };

  const handleArea = (e: any) => {
    setSelectedArea(e?.area || "");
    setSelectedPrice(getDisplayPrice(e?.price));
  };

  const handleShowPackages = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowPackages(!showPackages);
  };

  const rating = starRating ? parseFloat(starRating) : 0;
  const showPackagesButton = areaPrice && areaPrice.length > 1;
  const showBookNowButton = showBookNowDynamic(city, cities) && !showPackagesButton;
  const selectedPitch = areaPrice?.find(
    (pitch: any) => pitch?.pitchName === selectedPitchName
  );
  const selectedCardSport = activeSport || getPitchSports(selectedPitch)[0];
  const isSelectedSportClosed =
    !!selectedCardSport &&
    !isSportEnabledAtTurf(selectedCardSport, data?.enabledSports);

  return (
    <div
      ref={cardRef}
      className={`card-container ${isUnavailable ? "unavailable" : ""}`}
      onMouseEnter={() => {
        setIsHovered(true);
        // Cache bounding rect once on mouse enter to avoid forced reflows
        if (cardRef.current) {
          rectCache.current = cardRef.current.getBoundingClientRect();
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        rectCache.current = null;
        if (hoverRef.current) {
          hoverRef.current.style.transform = "translate(0, 0)";
          hoverRef.current.style.opacity = "0";
        }
      }}
      onMouseMove={(e) => {
        if (!rectCache.current || !hoverRef.current) return;
        // Use cached rect and CSS transform for better performance
        const x = e.clientX - rectCache.current.x;
        const y = e.clientY - rectCache.current.y;
        hoverRef.current.style.transform = `translate(${x}px, ${y}px)`;
        hoverRef.current.style.opacity = "1";
      }}
      onClick={handleCardClick}
    >
      <div className="hover" ref={hoverRef}></div>

      {isUnavailable && isHovered && <div className="under-maintainence">Under Maintenance</div>}

      <CarouselModel
        name={name}
        imageUrl={activeSport && sportImages?.[activeSport]?.length ? sportImages[activeSport] : imageUrl}
        isHovered={isHovered}
        imgHeight="300px"
        turf={{ ...data, name: userName }}
        contact={partnership ? data?.displayContact : data?.actualContact}
        starRating={starRating}
        isLCP={isLCP}
      />

      {partnership && (
        <Tooltip title="Book our partnered turf to unlock exclusive offers! Enjoy special discounts and benefits just for you.">
          <div className="partnership-flag">Partnered</div>
        </Tooltip>
      )}

      {distanceLabel ? (
        <div className="turf-card-distance-banner" role="status">
          {distanceLabel}
        </div>
      ) : null}

      {!showPackages && (
        <TurfInfoList
          name={name}
          fields={req_fields}
          data={data}
          rating={rating}
          handleArea={handleArea}
          userName={userName}
          city={city}
        />
      )}

      {showPackagesButton && showPackages && (
        <div className="packages-container">
          {areaPrice?.map((el: any, i: number) => {
            const dimensions = getPitchDimensionsLabel(el);
            const pitchSport = activeSport || getPitchSports(el)[0];
            const isPitchSportClosed =
              !!pitchSport &&
              !isSportEnabledAtTurf(pitchSport, data?.enabledSports);
            return (
            <div
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                handleTurfCardClick(i);
              }}
            >
              <Card bordered>
                <Title level={5}>
                  Pitch available - {getPitchSpecificationLabel(el, i)}
                </Title>
                {dimensions && <Title level={5}>Dimensions - {dimensions}</Title>}
                <Title level={5}>Starting @ {getDisplayPrice(el?.price)}/hr</Title>
                {showBookNowDynamic(city, cities) && (
                  <Button
                    block
                    type="primary"
                    disabled={isUnavailable || isPitchSportClosed}
                    onClick={(e) => handleBookNow(e, el?.pitchName)}
                  >
                    {isPitchSportClosed ? "Currently Closed" : "Check Availability"}
                  </Button>
                )}
              </Card>
            </div>
            );
          })}
        </div>
      )}

      {showBookNowButton && (
        <Button
          className="book-now-btn"
          block
          type="primary"
          size="large"
          disabled={isUnavailable || isSelectedSportClosed}
          onClick={(e) => handleBookNow(e, selectedPitchName)}
        >
          {isSelectedSportClosed ? "Currently Closed" : "Check Availability"}
        </Button>
      )}

      {showPackagesButton &&
        (!showPackages ? (
          <Button block type="primary" size="large" disabled={isUnavailable} onClick={handleShowPackages}>
            Show Pitches ({areaPrice?.length || 0})
          </Button>
        ) : (
          <Button
            type="link"
            onClick={handleShowPackages}
            disabled={isUnavailable}
            style={{ display: "block", padding: 0, color: "#1890ff", textAlign: "center", fontSize: "1.3em" }}
          >
            &lt; back to overview
          </Button>
        ))}
    </div>
  );
};

// OPTIMIZED: Memoize the Tile component to prevent unnecessary re-renders
// This is critical for performance when rendering lists of cards
const MemoizedTile = memo(Tile, (prevProps, nextProps) => {
  return (
    prevProps.userName === nextProps.userName &&
    prevProps.city === nextProps.city &&
    prevProps.index === nextProps.index &&
    prevProps.isUnavailable === nextProps.isUnavailable &&
    prevProps.name === nextProps.name &&
    prevProps.distanceLabel === nextProps.distanceLabel &&
    prevProps.activeSport === nextProps.activeSport &&
    prevProps.data?.starRating === nextProps.data?.starRating &&
    prevProps.data?.partnership === nextProps.data?.partnership &&
    JSON.stringify(prevProps.data?.enabledSports) === JSON.stringify(nextProps.data?.enabledSports) &&
    JSON.stringify(prevProps.data?.areaPrice) === JSON.stringify(nextProps.data?.areaPrice)
  );
});

export default MemoizedTile;
